import React, { forwardRef } from 'react'
import './InputField.css'

export interface InputFieldProps {
  label?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url'
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  error?: string
  success?: boolean
  required?: boolean
  fullWidth?: boolean
  className?: string
  id?: string
  name?: string
  autoComplete?: string
  maxLength?: number
  minLength?: number
  pattern?: string
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  size = 'medium',
  disabled = false,
  error,
  success = false,
  required = false,
  fullWidth = false,
  className = '',
  id,
  name,
  autoComplete,
  maxLength,
  minLength,
  pattern,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value)
    }
  }

  const inputClasses = [
    'input-field',
    `input-${size}`,
    fullWidth ? 'input-full-width' : '',
    error ? 'input-error' : '',
    success ? 'input-success' : '',
    disabled ? 'input-disabled' : '',
    className
  ].filter(Boolean).join(' ')

  return (
    <div className="input-container">
      {label && (
        <label htmlFor={inputId} className="input-label">
          {label}
          {required && <span className="input-required">*</span>}
        </label>
      )}
      
      <input
        ref={ref}
        id={inputId}
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        name={name}
        autoComplete={autoComplete}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        className={inputClasses}
        {...props}
      />
      
      {error && (
        <div className="input-error-message">
          {error}
        </div>
      )}
      
      {success && !error && (
        <div className="input-success-message">
          ✓ Valid
        </div>
      )}
    </div>
  )
})

InputField.displayName = 'InputField'

export default InputField 